import { envConfig, SchemaOrgItemType } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import {
    CommonDI,
    PromoStandardTextRepositoryType,
    ProductRecommendationsRepositoryType,
} from '@headless-workspace/domain/common/DI';
import {
    AsyncComponent,
    ProductDetailValue,
    PromotionInformationType,
    PromotionType,
    VipBrandsValue,
    getPromotionInfoServerAction,
    ProductSetItemValue,
} from '@headless-workspace/domain/common/server';
import { PropsWithLocale } from '@headless-workspace/glow-ds';
import { ProductDetailCarouselContainer, ProductDetailContainerSection } from './../../../client';
import { ProductDetailFooterSection } from './ProductDetailFooterSection';
import { ProductDetailBrandCornerSection } from './brandCorner';
import { ProductDetailRatingsAndReviewsSection } from './ratingsAndReviews';

type ProductDetailTemplateProps = PropsWithLocale & {
    product: ProductDetailValue;
    vipBrands: VipBrandsValue;
    redDotText?: string;
    enablePriceExceptionLegalMentions: boolean;
};

export const PRODUCT_DETAIL_TEMPLATE_ID = 'product-detail-template';
export const DATA_PRODUCT_ID = 'data-product-id';
export const DATA_PRODUCT_SKU = 'data-product-sku';

const getProductPromotions = async (
    promotionList: PromotionType[],
): Promise<Record<PromotionType, PromotionInformationType>> => {
    const promotionsList = await Promise.all(
        promotionList.map(async (promotionKey) => ({
            key: promotionKey,
            promotionInfo: unwrapResultSuccess(
                await getPromotionInfoServerAction(promotionKey === PromotionType.OTHER ? undefined : promotionKey),
            ),
        })),
    );

    return promotionsList.reduce(
        (acc, { key, promotionInfo }) => ({
            ...acc,
            ...(promotionInfo ? { [key]: promotionInfo } : {}),
        }),
        {} as Record<PromotionType, PromotionInformationType>,
    );
};

export const getProductDetailPromotionInformations = async (
    promotion?: PromotionType,
    productsSetList?: ProductSetItemValue[],
) => {
    const productSetPromotions =
        productsSetList?.map((productSet) => productSet.promotion).filter((promotion) => promotion) ?? [];

    const promotionsKeys = Array.from(
        new Set([promotion, ...productSetPromotions].filter(Boolean)) as Set<PromotionType>,
    );

    const promotionInfosMap = await getProductPromotions(promotionsKeys);

    return {
        promotionInfosMap,
    };
};

export const ProductDetailTemplate: AsyncComponent<ProductDetailTemplateProps> = async ({
    product,
    locale,
    vipBrands,
    redDotText,
    enablePriceExceptionLegalMentions,
}) => {
    const { promotionInfosMap } = await getProductDetailPromotionInformations(
        product.promotion,
        product.productsSetList,
    );

    const promoStandardTextResult = await CommonDI.get(PromoStandardTextRepositoryType).getPromoStandardText(locale);
    const originalPriceText = unwrapResultSuccess(promoStandardTextResult)?.originalPriceText;

    const productRecommendationResult = await CommonDI.get(
        ProductRecommendationsRepositoryType,
    ).getProductRecommendation(locale, product.id, envConfig.headlessPdpT2sPageId, vipBrands, originalPriceText);

    const productRecommendationSuccess = unwrapResultSuccess(productRecommendationResult);

    const fontFamily = product.isArialFont ? 'arial' : 'brand';

    const dataAttributes = {
        [DATA_PRODUCT_ID]: product.id,
        [DATA_PRODUCT_SKU]: product.sku,
    };

    return (
        <section
            id={PRODUCT_DETAIL_TEMPLATE_ID}
            className={'flex flex-col gap-2 desktop:gap-4'}
            itemScope
            itemType={SchemaOrgItemType.Product}
            {...dataAttributes}
        >
            <ProductDetailContainerSection
                promotionInfosMap={promotionInfosMap}
                product={product}
                locale={locale}
                vipBrands={vipBrands}
                fontFamily={fontFamily}
                redDotText={redDotText}
            />
            {productRecommendationSuccess && productRecommendationSuccess.length > 0 && (
                <ProductDetailCarouselContainer
                    fontFamily={fontFamily}
                    locale={locale}
                    vipBrands={vipBrands}
                    products={productRecommendationSuccess[0].products}
                    title={productRecommendationSuccess[0].title}
                    button={productRecommendationSuccess[0].cta}
                    enablePriceExceptionLegalMentions={enablePriceExceptionLegalMentions}
                />
            )}
            <ProductDetailRatingsAndReviewsSection rating={product.rating} productId={product.id} locale={locale} />
            {productRecommendationSuccess && productRecommendationSuccess.length === 2 && (
                <ProductDetailCarouselContainer
                    fontFamily={fontFamily}
                    locale={locale}
                    vipBrands={vipBrands}
                    products={productRecommendationSuccess[1].products}
                    title={productRecommendationSuccess[1].title}
                    button={productRecommendationSuccess[1].cta}
                    enablePriceExceptionLegalMentions={enablePriceExceptionLegalMentions}
                />
            )}
            <ProductDetailBrandCornerSection fontFamily={fontFamily} />
            <ProductDetailFooterSection />
        </section>
    );
};
